import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";

import { ArrowUpLeftIcon, DropdownListItem, Toggle } from "@prequel/shared-components";

import NavigationItem from "./NavigationItem";
import ProfileMenu from "./ProfileMenu";

import { useLogout, useUpdateEnvironment } from "@/api/queries/auth";
import { Logo } from "@/assets/logos";
import { dropdownEnvironments, Environment, useAuth } from "@/providers/AuthProvider";

const ExportNavigationItems = () => (
  <nav className="mt-3 flex-0 space-y-1.5 border-b border-slate-200 px-3 pb-4">
    <NavigationItem to="/overview" title="Overview" />
    <NavigationItem to="/activity" title="Activity" />
    <NavigationItem to="/sources" title="Sources" />
    <NavigationItem to="/models" title="Models & Products" />
    <NavigationItem to="/recipients" title="Recipients" />
    <NavigationItem to="/destinations" title="Destinations" />
    <NavigationItem to="/magic-links" title="Magic Links" />
    <NavigationItem to="/webhooks" title="Webhooks" />
  </nav>
);

const ImportNavigationItems = () => (
  <>
    <nav className="flex-0 border-b border-slate-200 p-3">
      <NavLink
        to="/overview"
        className="
          flex items-center rounded-sm px-3 py-1 text-sm font-normal text-slate-600
          hover:bg-slate-100 hover:text-slate-900
        "
      >
        <ArrowUpLeftIcon className="mr-2 size-4 text-slate-400" />
        Return to Export
      </NavLink>
    </nav>
    <nav className="mt-3 flex-0 space-y-1.5 border-b border-slate-200 px-3 pb-4">
      <NavigationItem to="/import/overview" title="Overview" />
      <NavigationItem to="/import/activity" title="Activity" />
      <NavigationItem to="/import/datalakes" title="Datalakes" />
      <NavigationItem to="/import/providers" title="Providers" />
      <NavigationItem to="/import/sources" title="Sources" />
      <NavigationItem to="/import/datasets" title="Datasets & Extracts" />
      <NavigationItem to="/import/streams" title="Streams & Loads" />
      <NavigationItem to="/import/destinations" title="Destinations & Products" />
    </nav>
  </>
);

const Navigation = () => {
  const { environment } = useAuth();
  const [currentEnvironment, setCurrentEnvironment] = useState<DropdownListItem<Environment>>(dropdownEnvironments[0]);
  const location = useLocation();

  const navigate = useNavigate();
  const logout = useLogout();
  const updateEnvironment = useUpdateEnvironment();
  const isImportPage = location.pathname.startsWith("/import");

  const onLogoutHandler = () => logout.mutate();

  useEffect(() => {
    const e = dropdownEnvironments.find(({ key }) => key === environment);
    if (e) {
      setCurrentEnvironment(e);
    }
  }, [environment]);
  return (
    <div className="inset-y-0 flex w-64 flex-col bg-neutral-50">
      <div className="flex min-h-0 flex-1 flex-col border-r border-slate-200">
        <div className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex h-12 shrink-0 items-center border-b border-slate-200 px-6">
            <Logo className="h-4" />
          </div>
          {isImportPage ? <ImportNavigationItems /> : <ExportNavigationItems />}
          {!isImportPage && (
            <nav className="mt-3 flex-1 space-y-1.5 border-b border-slate-200 px-3 pb-4">
              <h1 className="flex cursor-default items-center rounded-sm px-3 py-1 text-sm font-normal text-slate-400">
                Documentation
              </h1>
              <NavigationItem to="https://docs.prequel.co/export" title="Guides" isExternal />
              <NavigationItem
                to="https://docs.prequel.co/export/api-reference/overview"
                title="API Reference"
                isExternal
              />
            </nav>
          )}
        </div>

        <div className="sticky bottom-0 flex-0 border-t border-slate-200 bg-neutral-50">
          <div className="space-y-3 py-4">
            <Toggle
              label={
                <span className="font-normal text-slate-400">
                  Env. <span className="text-slate-600">{currentEnvironment.text}</span>
                </span>
              }
              checked={currentEnvironment.key === Environment.PROD}
              setChecked={(isEnabled: boolean) =>
                updateEnvironment.mutate({
                  environment: isEnabled ? Environment.PROD : Environment.STAGING,
                })
              }
              className="w-full justify-between px-6"
              size="sm"
            />
            <ProfileMenu
              menuItems={[
                { text: "Settings", onClick: () => navigate("/settings") },
                { text: "Logout", onClick: onLogoutHandler },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navigation;
